// @ts-nocheck
import { Stack, Title, Text, SimpleGrid, Code } from '@mantine/core';
import { Card } from '../../../../components/ui';
import { AreaChartWidget } from '../../../../components/advanced/Widgets';
import { mockAreaChartData } from '../../mockData/widgets';

export default function AreaChartWidgetsPage() {
  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>Area Chart Widgets</Title>
        <Text c="dimmed" size="sm">
          Volume changes over time visualization widgets. Perfect for showing cumulative trends,
          revenue streams, and stacked data comparisons.
        </Text>
      </Stack>

      {/* Single Area Chart */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Single Area Chart</Title>
          <Text size="sm" c="dimmed">
            Basic area chart showing volume changes over time with filled gradient.
          </Text>

          <AreaChartWidget
            title="Total Revenue"
            subtitle="Last 6 months"
            data={mockAreaChartData.slice(0, 180)}
            areas={[
              {
                dataKey: 'subscriptions',
                name: 'Total Revenue',
                color: '#228be6',
                fillOpacity: 0.7,
              },
            ]}
            height={350}
            showExport
          />

          <Code block>
{`<AreaChartWidget
  title="Total Revenue"
  data={revenueData}
  areas={[
    {
      dataKey: 'subscriptions',
      name: 'Total Revenue',
      color: '#228be6',
      fillOpacity: 0.7
    }
  ]}
  showExport
/>`}
          </Code>
        </Stack>
      </Card>

      {/* Multi-Series Area Chart */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Multi-Series Area Chart (Unstacked)</Title>
          <Text size="sm" c="dimmed">
            Multiple area series overlapping to compare different metrics.
          </Text>

          <AreaChartWidget
            title="Revenue Comparison"
            subtitle="Comparing revenue streams"
            data={mockAreaChartData.slice(0, 90)}
            areas={[
              {
                dataKey: 'subscriptions',
                name: 'Subscriptions',
                color: '#228be6',
                fillOpacity: 0.4,
              },
              {
                dataKey: 'oneTimePurchases',
                name: 'One-time Purchases',
                color: '#40c057',
                fillOpacity: 0.4,
              },
              {
                dataKey: 'services',
                name: 'Services',
                color: '#7950f2',
                fillOpacity: 0.4,
              },
            ]}
            stacked={false}
            height={350}
            showExport
            showLegend
          />

          <Code block>
{`<AreaChartWidget
  title="Revenue Comparison"
  data={multiStreamData}
  areas={[
    { dataKey: 'subscriptions', name: 'Subscriptions', color: '#228be6' },
    { dataKey: 'oneTime', name: 'One-time', color: '#40c057' },
    { dataKey: 'services', name: 'Services', color: '#7950f2' }
  ]}
  stacked={false}
  showLegend
/>`}
          </Code>
        </Stack>
      </Card>

      {/* Stacked Area Chart */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Stacked Area Chart</Title>
          <Text size="sm" c="dimmed">
            Stacked areas showing how different components contribute to a total over time.
          </Text>

          <AreaChartWidget
            title="Revenue Streams"
            subtitle="Monthly breakdown"
            data={mockAreaChartData.slice(0, 90)}
            areas={[
              {
                dataKey: 'subscriptions',
                name: 'Subscriptions',
                color: '#228be6',
                fillOpacity: 0.6,
              },
              {
                dataKey: 'oneTimePurchases',
                name: 'One-time',
                color: '#40c057',
                fillOpacity: 0.6,
              },
              {
                dataKey: 'services',
                name: 'Services',
                color: '#7950f2',
                fillOpacity: 0.6,
              },
            ]}
            stacked
            height={350}
            showExport
            showLegend
          />

          <Code block>
{`<AreaChartWidget
  title="Revenue Streams"
  data={revenueData}
  areas={[
    { dataKey: 'subscriptions', name: 'Subscriptions', color: '#228be6' },
    { dataKey: 'oneTime', name: 'One-time', color: '#40c057' },
    { dataKey: 'services', name: 'Services', color: '#7950f2' }
  ]}
  stacked  // Key difference: stacked areas
  showLegend
/>`}
          </Code>
        </Stack>
      </Card>

      {/* Comparison: Stacked vs Unstacked */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Stacked vs Unstacked Comparison</Title>
          <Text size="sm" c="dimmed">
            Same data shown in both modes to illustrate the difference.
          </Text>

          <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
            <AreaChartWidget
              title="Unstacked Areas"
              data={mockAreaChartData.slice(0, 60)}
              areas={[
                {
                  dataKey: 'subscriptions',
                  name: 'Subscriptions',
                  color: '#228be6',
                  fillOpacity: 0.4,
                },
                {
                  dataKey: 'oneTimePurchases',
                  name: 'One-time',
                  color: '#40c057',
                  fillOpacity: 0.4,
                },
              ]}
              stacked={false}
              height={300}
            />

            <AreaChartWidget
              title="Stacked Areas"
              data={mockAreaChartData.slice(0, 60)}
              areas={[
                {
                  dataKey: 'subscriptions',
                  name: 'Subscriptions',
                  color: '#228be6',
                  fillOpacity: 0.6,
                },
                {
                  dataKey: 'oneTimePurchases',
                  name: 'One-time',
                  color: '#40c057',
                  fillOpacity: 0.6,
                },
              ]}
              stacked
              height={300}
            />
          </SimpleGrid>

          <Code block>
{`// Unstacked - See each metric independently
<AreaChartWidget data={data} areas={areas} stacked={false} />

// Stacked - See cumulative total
<AreaChartWidget data={data} areas={areas} stacked={true} />`}
          </Code>
        </Stack>
      </Card>

      {/* Custom Opacity */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Custom Fill Opacity</Title>
          <Text size="sm" c="dimmed">
            Adjust fillOpacity to control the transparency of area fills.
          </Text>

          <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
            <AreaChartWidget
              title="High Opacity (0.8)"
              data={mockAreaChartData.slice(0, 60)}
              areas={[
                {
                  dataKey: 'subscriptions',
                  name: 'Revenue',
                  color: '#12b886',
                  fillOpacity: 0.8,
                },
              ]}
              height={300}
            />

            <AreaChartWidget
              title="Low Opacity (0.3)"
              data={mockAreaChartData.slice(0, 60)}
              areas={[
                {
                  dataKey: 'subscriptions',
                  name: 'Revenue',
                  color: '#12b886',
                  fillOpacity: 0.3,
                },
              ]}
              height={300}
            />
          </SimpleGrid>

          <Code block>
{`<AreaChartWidget
  areas={[
    { dataKey: 'revenue', fillOpacity: 0.8 }  // More solid
  ]}
/>

<AreaChartWidget
  areas={[
    { dataKey: 'revenue', fillOpacity: 0.3 }  // More transparent
  ]}
/>`}
          </Code>
        </Stack>
      </Card>

      {/* Use Cases */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Common Use Cases</Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <Stack gap="xs">
              <Text size="sm" fw={500}>Revenue Tracking</Text>
              <Text size="xs" c="dimmed">
                Track total revenue over time or break down revenue streams in stacked format.
              </Text>
            </Stack>
            <Stack gap="xs">
              <Text size="sm" fw={500}>Traffic Analysis</Text>
              <Text size="xs" c="dimmed">
                Visualize website traffic volume, showing peaks and valleys over time.
              </Text>
            </Stack>
            <Stack gap="xs">
              <Text size="sm" fw={500}>Resource Usage</Text>
              <Text size="xs" c="dimmed">
                Monitor cumulative resource consumption like storage, bandwidth, or API calls.
              </Text>
            </Stack>
            <Stack gap="xs">
              <Text size="sm" fw={500}>Budget Spending</Text>
              <Text size="xs" c="dimmed">
                Show spending trends over time, with stacked areas for different expense categories.
              </Text>
            </Stack>
          </SimpleGrid>
        </Stack>
      </Card>
    </Stack>
  );
}
